/**
 * FlagCard Component
 *
 * Displays a content flag with details and action buttons for moderators.
 */

import { type CSSProperties } from 'react';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { FLAG_REASON_LABELS, type ContentFlag, type FlagStatus } from '@flash/shared';

/**
 * Props for FlagCard component
 */
interface FlagCardProps {
  /** The content flag to display */
  flag: ContentFlag;
  /** Called when resolve button is clicked */
  onResolve: () => void;
  /** Called when dismiss button is clicked */
  onDismiss: () => void;
}

/**
 * Get status badge style and label
 */
const getStatusInfo = (
  status: FlagStatus
): { label: string; style: CSSProperties; icon: React.ReactNode } => {
  switch (status) {
    case 'pending':
      return {
        label: 'Pending',
        style: {
          backgroundColor: 'var(--color-badge-warning-bg)',
          color: 'var(--color-badge-warning-text)',
        },
        icon: <AccessTimeIcon fontSize="small" />,
      };
    case 'reviewed':
      return {
        label: 'Reviewed',
        style: {
          backgroundColor: 'var(--color-badge-info-bg)',
          color: 'var(--color-badge-info-text)',
        },
        icon: <VisibilityIcon fontSize="small" />,
      };
    case 'actioned':
      return {
        label: 'Actioned',
        style: {
          backgroundColor: 'var(--color-badge-success-bg)',
          color: 'var(--color-badge-success-text)',
        },
        icon: <CheckCircleIcon fontSize="small" />,
      };
    case 'dismissed':
      return {
        label: 'Dismissed',
        style: {
          backgroundColor: 'var(--color-bg-tertiary)',
          color: 'var(--color-text-secondary)',
        },
        icon: <CancelIcon fontSize="small" />,
      };
    default:
      return {
        label: status,
        style: {},
        icon: <FlagIcon fontSize="small" />,
      };
  }
};

/**
 * Format timestamp for display
 */
const formatDate = (timestamp: unknown): string => {
  if (!timestamp) return 'Unknown';

  // Handle Firestore Timestamp
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    const date = (timestamp as { toDate: () => Date }).toDate();
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return 'Unknown';
};

/**
 * FlagCard component for displaying flagged content
 */
/**
 * Action labels for display
 */
const ACTION_LABELS: Record<string, string> = {
  removed: 'Content Removed',
  hidden: 'Content Hidden',
  warned: 'User Warned',
  banned: 'User Banned',
  none: 'No Action Taken',
};

/**
 * FlagCard component for displaying flagged content
 */
export const FlagCard = ({ flag, onResolve, onDismiss }: FlagCardProps) => {
  const statusInfo = getStatusInfo(flag.status);
  const isPending = flag.status === 'pending';
  const isActionable = isPending; // Only pending flags are actionable

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={headerLeftStyle}>
          <FlagIcon style={{ color: 'var(--color-error)', marginRight: '8px' }} />
          <span style={contentTypeStyle}>{flag.contentType}</span>
          <span
            style={{
              ...statusBadgeStyle,
              ...statusInfo.style,
            }}
          >
            {statusInfo.icon}
            <span style={{ marginLeft: '4px' }}>{statusInfo.label}</span>
          </span>
        </div>
        <span style={dateStyle}>{formatDate(flag.createdAt)}</span>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {/* Reason */}
        <div style={fieldStyle}>
          <span style={labelStyle}>Reason:</span>
          <span style={valueStyle}>{FLAG_REASON_LABELS[flag.reason]}</span>
        </div>

        {/* Reporter */}
        <div style={fieldStyle}>
          <span style={labelStyle}>Reported by:</span>
          <span style={valueStyle}>{flag.reporterDisplayName}</span>
        </div>

        {/* Content ID */}
        <div style={fieldStyle}>
          <span style={labelStyle}>Content ID:</span>
          <span style={contentIdStyle}>{flag.contentId}</span>
        </div>

        {/* Details (if provided) */}
        {flag.details && (
          <div style={detailsContainerStyle}>
            <span style={labelStyle}>Details:</span>
            <p style={detailsTextStyle}>{flag.details}</p>
          </div>
        )}

        {/* Resolution info (if actioned or reviewed) */}
        {(flag.status === 'actioned' || flag.status === 'reviewed') && flag.resolution && (
          <div style={resolutionContainerStyle}>
            <span style={labelStyle}>Resolution:</span>
            <span style={resolutionValueStyle}>
              {ACTION_LABELS[flag.resolution.action] ?? flag.resolution.action}
            </span>
            {flag.resolution.note && <p style={reviewNotesStyle}>{flag.resolution.note}</p>}
          </div>
        )}

        {/* Dismissal notes (if dismissed) */}
        {flag.status === 'dismissed' && flag.resolution?.note && (
          <div style={resolutionContainerStyle}>
            <span style={labelStyle}>Dismissal notes:</span>
            <p style={reviewNotesStyle}>{flag.resolution.note}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      {isActionable && (
        <div style={actionsStyle}>
          <button onClick={onDismiss} style={dismissButtonStyle}>
            <CancelIcon fontSize="small" />
            <span>Dismiss</span>
          </button>
          <button onClick={onResolve} style={resolveButtonStyle}>
            <CheckCircleIcon fontSize="small" />
            <span>Take Action</span>
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Component styles
 */
const cardStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-secondary)',
};

const headerLeftStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const contentTypeStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  textTransform: 'capitalize',
  marginRight: '12px',
};

const statusBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 10px',
  borderRadius: '9999px',
  fontSize: '0.75rem',
  fontWeight: 500,
};

const dateStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
};

const contentStyle: CSSProperties = {
  padding: '20px',
};

const fieldStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
};

const labelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  marginRight: '8px',
  minWidth: '100px',
};

const valueStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
};

const contentIdStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  fontFamily: 'monospace',
  backgroundColor: 'var(--color-bg-secondary)',
  padding: '2px 6px',
  borderRadius: '4px',
};

const detailsContainerStyle: CSSProperties = {
  marginTop: '16px',
  paddingTop: '16px',
  borderTop: '1px solid var(--color-border)',
};

const detailsTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  lineHeight: 1.6,
  margin: '8px 0 0 0',
  padding: '12px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
};

const resolutionContainerStyle: CSSProperties = {
  marginTop: '16px',
  paddingTop: '16px',
  borderTop: '1px solid var(--color-border)',
};

const resolutionValueStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  textTransform: 'capitalize',
  fontWeight: 500,
};

const reviewNotesStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.6,
  margin: '8px 0 0 0',
  fontStyle: 'italic',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '16px 20px',
  borderTop: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-secondary)',
};

const dismissButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 16px',
  backgroundColor: 'transparent',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const resolveButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 16px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

export default FlagCard;
